import React, { useState, useCallback } from 'react';
import styles from '../index.module.css';
import { AIBanner } from 'appJS/views/new_project_settings/AIBanner';
import { Alert, Button, Form } from 'antd';
import * as translations from '../../new_project_settings/strings';
import { getLangKey } from '../../../models/Language';
import HelpArticleButton from '../../../utils/HelpArticleButton';
import {
  AISettingsForm,
  fieldNames,
  handleEnableOrDisableBeta
} from '../../new_project_settings/AISettingsForm';
import { updateOrganization } from '../organization';
import { AIBetaOptIn } from 'appJS/views/new_project_settings/AIBetaOptIn';

const strings = translations[getLangKey()];

type Props = {
  org: {
    ai_suggested_tags: boolean;
    ai_suggested_titles: boolean;
    ai_duplicate_tasks: boolean;
    ai_suggested_tags_existing_only?: boolean;
    ai_suggested_titles_default_language?: string;
    id: number;
  };
  aiFeatures: boolean;
  duplicateTasksExperiment: boolean;
  container: HTMLDivElement;
};

export const AIOrgSettings = ({
  org,
  aiFeatures,
  duplicateTasksExperiment,
  container
}: Props) => {
  const [form] = Form.useForm();
  const { getFieldValue, isFieldTouched } = form;
  const [organization, setOrganization] = useState(org);
  const [, updateState] = useState<{}>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const [loading, setLoading] = useState(false);
  const [aiFeaturesEnabled, setAiFeaturesEnabled] = useState<boolean>(
    aiFeatures
  );

  const submit = () => {
    updateOrganization({
      organizationId: organization.id,
      body: fieldNames(duplicateTasksExperiment).reduce((acc, fieldName) => {
        if (isFieldTouched(fieldName))
          acc[fieldName] = getFieldValue(fieldName);
        return acc;
      }, {}),
      onSuccess: response => {
        setOrganization(previousValue => ({
          ...previousValue,
          ...response.organization
        }));
      },
      showSuccess: true,
      onFinish: () => setLoading(false)
    });
    setLoading(true);
  };

  return (
    <div className={styles.aiSettingsContainer}>
      <AIBanner inAiBeta={aiFeaturesEnabled} orgSettings />
      {aiFeaturesEnabled ? (
        <>
          <Alert
            message={
              <div className={styles.alertMessageContainer}>
                <span className={styles.alertMessage}>
                  {strings.overrideProjectSettings}
                </span>
                <HelpArticleButton articleId={12686483}>
                  <Button size="small" type="link">
                    {strings.moreInfo}
                  </Button>
                </HelpArticleButton>
              </div>
            }
            className={styles.aiSettingsAlert}
            type="info"
            showIcon
          />
          <AISettingsForm
            form={form}
            submit={submit}
            forceUpdate={forceUpdate}
            organization={organization}
            loading={loading}
            onOptInOrOut={setAiFeaturesEnabled}
            container={container}
            duplicateTasksExperiment={duplicateTasksExperiment}
          />
        </>
      ) : (
        <AIBetaOptIn
          handleEnableBeta={() =>
            handleEnableOrDisableBeta({
              organizationId: organization.id,
              enable: true,
              callback: () => setAiFeaturesEnabled(true)
            })
          }
        />
      )}
    </div>
  );
};
